import { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import { FiDownload } from 'react-icons/fi';
import { formatDate } from '../../../../utils/collateralFormatters';
import useLegalCaseDeposits, {
    type AddJudicialDepositValue,
} from '../../../../hooks/useLegalCaseDeposits';
import type {
    JudicialDeposit,
    JudicialDepositSummary,
    UpdateJudicialDepositPayload,
} from '../../../../types/LegalCase';
import LegalCaseDepositForm from './LegalCaseDepositForm';
import LegalCaseWithdrawalForm from './LegalCaseWithdrawalForm';
import ConfirmDialog from './ConfirmDialog';

interface LegalCaseDepositsProps {
    caseId: string;
    initialDeposits?: JudicialDeposit[];
    initialSummary?: JudicialDepositSummary;
}

// Selo do estado do depósito: levantado em verde (recuperado efetivo), bloqueado em âmbar
// (ainda em custódia). O estado vem do backend (`withdrawn` + `statusFormatted`).
const statusClass = (withdrawn: boolean): string =>
    withdrawn
        ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
        : 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300';

// Card do agregado: distingue bloqueado de levantado (a resposta da diretoria "quanto está
// preso e quanto foi recuperado"). Extraído para fora do componente (diretriz: sem função
// dentro de função).
const SummaryCard = ({ summary }: { summary: JudicialDepositSummary }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3">
            <p className="text-[10px] uppercase text-gray-500 dark:text-gray-400">Total bloqueado</p>
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {summary.totalBlockedFormatted || 'R$ 0,00'}
            </p>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3">
            <p className="text-[10px] uppercase text-gray-500 dark:text-gray-400">Recuperado (levantado)</p>
            <p className="text-sm font-semibold text-green-700 dark:text-green-300">
                {summary.totalWithdrawnFormatted || 'R$ 0,00'}
            </p>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3">
            <p className="text-[10px] uppercase text-gray-500 dark:text-gray-400">Bloqueado sem levantamento</p>
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                {summary.blockedNotWithdrawnFormatted || 'R$ 0,00'}
            </p>
        </div>
    </div>
);

// Uma linha de depósito. Extraída para fora do componente. Depósito ainda bloqueado mostra o
// botão "Registrar levantamento"; o já levantado exibe o valor e a data do alvará.
const DepositItem = ({
    deposit,
    onRegisterWithdrawal,
    onRemove,
    disabled,
}: {
    deposit: JudicialDeposit;
    onRegisterWithdrawal: () => void;
    onRemove: () => void;
    disabled: boolean;
}) => (
    <li className="flex items-start justify-between gap-2 py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
        <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {deposit.blockedAmountFormatted || 'Valor a confirmar'}
                </span>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${statusClass(deposit.withdrawn)}`}>
                    {deposit.statusFormatted}
                </span>
                {deposit.blockedAt && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        Bloqueado em {formatDate(deposit.blockedAt)}
                    </span>
                )}
                {deposit.courtRecordsPage && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        {deposit.courtRecordsPage}
                    </span>
                )}
            </div>
            {deposit.withdrawn && (
                <div className="text-[10px] text-green-700 dark:text-green-300 mt-0.5">
                    Levantado {deposit.withdrawnAmountFormatted}
                    {deposit.withdrawnAt ? ` em ${formatDate(deposit.withdrawnAt)}` : ''}
                </div>
            )}
            {deposit.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">{deposit.description}</p>
            )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
            {!deposit.withdrawn && (
                <button
                    type="button"
                    onClick={onRegisterWithdrawal}
                    disabled={disabled}
                    title="Registrar levantamento"
                    className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900 rounded-lg transition-colors disabled:opacity-50"
                >
                    <FiDownload size={12} />
                </button>
            )}
            <button
                type="button"
                onClick={onRemove}
                disabled={disabled}
                title="Remover depósito"
                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50"
            >
                <FaTrash size={11} />
            </button>
        </div>
    </li>
);

// Seção "Depósitos" do detalhe do caso. Lista os depósitos judiciais com o agregado que
// distingue bloqueado de levantado (recuperado efetivo). Registrar o levantamento é uma edição
// do depósito (valor + data do alvará). A remoção passa por ConfirmDialog (padrão do módulo).
const LegalCaseDeposits = ({ caseId, initialDeposits, initialSummary }: LegalCaseDepositsProps) => {
    const deposits = useLegalCaseDeposits(caseId, initialDeposits, initialSummary);
    const [showAddForm, setShowAddForm] = useState(false);
    const [withdrawingId, setWithdrawingId] = useState<string | null>(null);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    // Algum formulário aberto (criação ou levantamento): trava as ações por-linha para não
    // trocar o depósito-alvo com um form já preenchido.
    const anyFormOpen = showAddForm || withdrawingId !== null;

    const openAddForm = () => {
        deposits.clearErrors();
        setWithdrawingId(null);
        setShowAddForm(true);
    };

    const closeForms = () => {
        deposits.clearErrors();
        setShowAddForm(false);
        setWithdrawingId(null);
    };

    const openWithdrawalForm = (depositId: string) => {
        deposits.clearErrors();
        setShowAddForm(false);
        setWithdrawingId(depositId);
    };

    const submitAdd = async (value: AddJudicialDepositValue): Promise<boolean> => {
        const ok = await deposits.addDeposit(value);
        if (ok) closeForms();
        return ok;
    };

    const submitWithdrawal = async (value: UpdateJudicialDepositPayload): Promise<boolean> => {
        if (!withdrawingId) return false;
        const ok = await deposits.updateDeposit(withdrawingId, value);
        if (ok) closeForms();
        return ok;
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await deposits.removeDeposit(pendingRemoval);
        setPendingRemoval(null);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Depósitos judiciais
                </h4>
                {!showAddForm && withdrawingId === null && (
                    <button
                        type="button"
                        onClick={openAddForm}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Depósito
                    </button>
                )}
            </div>

            {deposits.deposits.length > 0 && <SummaryCard summary={deposits.summary} />}

            {deposits.deposits.length === 0 && !showAddForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhum depósito registrado</p>
            )}

            {deposits.deposits.length > 0 && (
                <ul>
                    {deposits.deposits.map((deposit) => (
                        <DepositItem
                            key={deposit.id}
                            deposit={deposit}
                            onRegisterWithdrawal={() => openWithdrawalForm(deposit.id)}
                            onRemove={() => setPendingRemoval(deposit.id)}
                            // Trava as ações das linhas enquanto salva OU enquanto um form está
                            // aberto: impede abrir o levantamento de outro depósito com o form já
                            // aberto (evita reaproveitar valores digitados para outro id).
                            disabled={deposits.isSaving || anyFormOpen}
                        />
                    ))}
                </ul>
            )}

            {showAddForm && (
                <LegalCaseDepositForm
                    errorFields={deposits.errorFields}
                    isSaving={deposits.isSaving}
                    onSubmit={submitAdd}
                    onCancel={closeForms}
                />
            )}

            {withdrawingId !== null && (
                // key por withdrawingId: remonta o form ao trocar de depósito-alvo, zerando o
                // state interno — o valor digitado para um depósito não vaza para outro id.
                <LegalCaseWithdrawalForm
                    key={withdrawingId}
                    errorFields={deposits.errorFields}
                    isSaving={deposits.isSaving}
                    onSubmit={submitWithdrawal}
                    onCancel={closeForms}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover depósito"
                message="Remover este depósito do caso? Esta ação não pode ser desfeita."
                isBusy={deposits.isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseDeposits;
